import { useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Panel,
  PanelAction,
  PanelBody,
  PanelContent,
  PanelHeader,
  PanelIcon,
  PanelMeta,
  PanelTitle,
} from '@multaqa/ui/components/ui/panel'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { Tag } from '@multaqa/ui/components/ui/tag'
import { Building2Icon, PlusIcon, TagIcon, Trash2Icon, UsersIcon, XIcon } from 'lucide-react'
import { RecordField, RecordSection } from '@/components/record-section'
import { localizedText } from '@/lib/me'
import { type PaymentTermResource, paymentTermLabel } from '@/lib/payment-terms'
import { regionName } from '@/lib/region-name'
import type { SupplierResource } from '../api'

export function SupplierDetailSections({
  supplier,
  locale,
  paymentTerms,
  availableTags,
  selectedTagId,
  onAttachTag,
  onDetachTag,
  onRemoveContact,
  onAddContact,
}: {
  supplier: SupplierResource
  locale: 'en' | 'ar'
  paymentTerms: PaymentTermResource[]
  availableTags: NonNullable<SupplierResource['tags']>
  selectedTagId: string
  onAttachTag: (tagId: string) => void
  onDetachTag: (tagId: string) => void
  onRemoveContact: (contactId: string) => void
  onAddContact: () => void
}) {
  const { t } = useTranslation()
  const paymentTermId =
    'payment_terms_id' in supplier && typeof supplier.payment_terms_id === 'string'
      ? supplier.payment_terms_id
      : null
  const paymentTermName = paymentTermLabel(
    paymentTerms,
    paymentTermId,
    supplier.payment_terms,
    locale,
  )

  return (
    <div className="margin-sections section-stack" data-testid="supplier-detail-sections">
      <RecordSection
        title={t('supplier.details')}
        description={t('supplier.detailsDescription')}
        icon={<Building2Icon />}
        testId="supplier-detail-information"
      >
        <RecordField label={t('supplier.code')} value={supplier.code} mono />
        <RecordField label={t('supplier.country')} value={regionName(supplier.country, locale)} />
        <RecordField label={t('supplier.defaultCurrency')} value={supplier.default_currency} mono />
        <RecordField
          label={t('supplier.taxIdentifier')}
          value={supplier.tax_identifier || '—'}
          mono
        />
        <RecordField label={t('supplier.paymentTerms')} value={paymentTermName} />
        <RecordField
          label={t('supplier.address')}
          value={
            typeof supplier.address === 'object' && supplier.address
              ? localizedText(supplier.address, locale) || '—'
              : supplier.address || '—'
          }
        />
      </RecordSection>

      <Panel data-testid="supplier-detail-contacts">
        <PanelHeader>
          <PanelIcon>
            <UsersIcon />
          </PanelIcon>
          <div className="min-w-0">
            <PanelTitle role="heading" aria-level={2} className="w-fit">
              {t('supplier.contacts')}
            </PanelTitle>
            <PanelMeta className="mt-1">{t('supplier.contactsDescription')}</PanelMeta>
          </div>
          <PanelAction>
            <Button variant="outline" size="sm" onClick={onAddContact}>
              <PlusIcon className="me-1 size-4" />
              {t('supplier.addContact')}
            </Button>
          </PanelAction>
        </PanelHeader>
        <PanelContent>
          {supplier.contacts && supplier.contacts.length > 0 ? (
            <div className="divide-y divide-border-subtle">
              {supplier.contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between gap-4 px-bleed py-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-body">{contact.name}</span>
                      {contact.is_primary ? (
                        <Badge tone="neutral">{t('contact.primary')}</Badge>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-4 text-small text-muted-foreground">
                      {contact.role ? <span>{contact.role}</span> : null}
                      {contact.phone ? <span dir="ltr">{contact.phone}</span> : null}
                      {contact.email ? <span dir="ltr">{contact.email}</span> : null}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveContact(contact.id)}
                    aria-label={t('action.delete')}
                  >
                    <Trash2Icon className="size-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <PanelBody className="pt-0">
              <EmptyState
                title={t('supplier.noContacts')}
                description={t('supplier.noContactsDescription')}
              />
            </PanelBody>
          )}
        </PanelContent>
      </Panel>
      <Panel data-testid="supplier-detail-tags">
        <PanelHeader>
          <PanelIcon>
            <TagIcon />
          </PanelIcon>
          <div className="min-w-0">
            <PanelTitle role="heading" aria-level={2} className="w-fit">
              {t('supplier.tags')}
            </PanelTitle>
            <PanelMeta className="mt-1">{t('supplier.tagsDescription')}</PanelMeta>
          </div>
        </PanelHeader>
        <PanelBody className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {supplier.tags && supplier.tags.length > 0 ? (
              supplier.tags.map((tag) => (
                <Tag key={tag.id} className="flex items-center gap-1">
                  <span>{tag.name}</span>
                  <button
                    type="button"
                    onClick={() => onDetachTag(tag.id)}
                    className="hover:text-destructive focus:outline-none"
                    aria-label={t('supplier.detachTag')}
                  >
                    <XIcon className="size-3" />
                  </button>
                </Tag>
              ))
            ) : (
              <span className="text-small text-muted-foreground">{t('supplier.noTags')}</span>
            )}
          </div>

          {availableTags.length > 0 ? (
            <div className="pt-2">
              <Select value={selectedTagId} onValueChange={onAttachTag}>
                <SelectTrigger className="max-w-xs">
                  <SelectValue placeholder={t('supplier.attachTag')} />
                </SelectTrigger>
                <SelectContent>
                  {availableTags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </PanelBody>
      </Panel>
    </div>
  )
}
